import React, { useEffect, useState } from "react";
import moment from "moment";
import Button from "../controls/button";
import CustomPopover from "../popover/Popover";
import CustomButton from "../../components/controls/button/Button";
import { Cross, PrintIcon, ArrowDownMedium, WeekIcon, MonthIcon, SearchIcon } from "../../modules/default-svg";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import styles from "./ReportButton.module.scss";
import resourcePlannerService from "../../views/ResourcesPlanner/resourcePlanner.service";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import { X } from "lucide-react";
import Modal from "../controls/modal/Modal";
import Toggle from "../controls/toggle/Toggle";
import UserSelect from "../userSelect/UserSelect";
import IconInput from "../controls/input/iconInput/IconInput";
import resourcesReportApi from "../../store/services/resourcesReport.service";
import { UserSearchRefType } from "../../helpers/utils";

const DATE_RANGE_OPTIONS = [
    { key: "week", label: "This Week" },
    { key: "month", label: "This Month" },
    { key: "custom", label: "Custom" },
];

const VIEW_TYPES = [
    { value: 0, label: "Week", icon: <WeekIcon /> },
    { value: 1, label: "Month", icon: <MonthIcon /> },
];

const getDateRange = (key) => {
    const now = moment();
    switch (key) {
        case "week":
            return { start: now.clone().startOf("isoWeek"), end: now.clone().endOf("isoWeek") };
        case "month":
            return { start: now.clone().startOf("month"), end: now.clone().endOf("month") };
        default:
            return null;
    }
};

export default function ResourceDutyRosterReportButton({
    projectId,
    projectName,
    resources: initialResources,
    projectFilters, // optional { clientId, portfolioId, programId } – scopes the in-panel project search
}) {
    const [innerProject, setInnerProject] = useState(null); // { id, name } – used when projectId isn't passed in
    const [dateRangeKey, setDateRangeKey] = useState("week");
    const [customStartDate, setCustomStartDate] = useState(null);
    const [customEndDate, setCustomEndDate] = useState(null);
    const [viewType, setViewType] = useState(0);
    const [showWeekend, setShowWeekend] = useState(false);
    const [showUtilization, setShowUtilization] = useState(true);
    const [selectedResources, setSelectedResources] = useState([]);
    const [openPopover, setOpenPopover] = useState(false);
    const [openProjectPopover, setOpenProjectPopover] = useState(false);
    const [openDateRangePopover, setOpenDateRangePopover] = useState(false);
    const [openViewTypePopover, setOpenViewTypePopover] = useState(false);
    const [projectSearch, setProjectSearch] = useState("");
    const [projects, setProjects] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();

    const isProjectControlled = !!projectId;
    const effectiveProjectId = projectId || innerProject?.id || null;
    const effectiveProjectName = isProjectControlled ? projectName : innerProject?.name;

    const selectedDateRange = DATE_RANGE_OPTIONS.find((o) => o.key === dateRangeKey);
    const selectedViewType = VIEW_TYPES.find((v) => v.value === viewType);

    const isCustomRangeInvalid =
        dateRangeKey === "custom" && (!customStartDate || !customEndDate || customStartDate > customEndDate);

    useEffect(() => {
        setSelectedResources(initialResources || []);
    }, [initialResources]);

    useEffect(() => {
        if (openProjectPopover) {
            fetchProjects(projectSearch);
        }
    
    }, [projectSearch, openProjectPopover]);

    const fetchProjects = (search) => {
        resourcesReportApi
            .searchProjects(search, projectFilters || {})
            .then((res) => setProjects(res || []))
            .catch(() => setProjects([]));
    };

    const closeSubPopovers = () => {
        setOpenProjectPopover(false);
        setOpenDateRangePopover(false);
        setOpenViewTypePopover(false);
    };

    const handleClose = () => {
        setOpenPopover(false);
        closeSubPopovers();
    };

    const handleSelectProject = (project) => {
        setInnerProject({ id: project.id, name: project.name });
        setSelectedResources([]);
        setOpenProjectPopover(false);
    };

    const handleClearProject = () => {
        setInnerProject(null);
        setSelectedResources([]);
    };

    const handleReport = () => {
        if (!effectiveProjectId || isCustomRangeInvalid) return;

        let start;
        let end;
        if (dateRangeKey === "custom") {
            start = moment(customStartDate);
            end = moment(customEndDate);
        } else {
            const range = getDateRange(dateRangeKey);
            start = range.start;
            end = range.end;
        }

        setIsLoading(true);
        const payload = {
            ProjectId: effectiveProjectId,
            StartDate: start.format("YYYY-MM-DD"),
            EndDate: end.format("YYYY-MM-DD"),
            ViewType: viewType,
            ShowWeekend: showWeekend,
            ShowUtilization: showUtilization,
        };
        if (selectedResources.length > 0) payload.Resources = selectedResources.map((r) => r.id);

        resourcePlannerService
            .getDutyRosterReport(payload)
            .then((response) => {
                const pdfBlob = new Blob([response], { type: "application/pdf" });
                const blobUrl = URL.createObjectURL(pdfBlob);
                setReportData({
                    name: effectiveProjectName ? `${effectiveProjectName} - Duty Roster Report` : "Resource Duty Roster Report",
                    pdfData: blobUrl,
                });
            })
            .catch((error) => {
                console.error("Report generation failed:", error);
                dispatch(
                    generalActions.showToast({
                        title: "Report generation failed. Please try again.",
                        type: "error",
                    })
                );
            })
            .finally(() => {
                setIsLoading(false);
                handleClose();
            });
    };

    return (
        <div>
            <CustomPopover
                icon={
                    <Button
                        btnClass={`d_btn ms-12 d_btn_primary ${styles.reportButton}`}
                        icon={<PrintIcon />}
                        label="Report"
                        clickHandler={() => {
                            closeSubPopovers();
                            setOpenPopover(true);
                        }}
                    />
                }
                minWidth={340}
                PopSurfaceClass={`mt-1 ${styles.reportSurface}`}
                status={openPopover}
            >
                <div className={`text-[#342c82] ${styles.dutyRosterPanel}`}>
                    <div className={styles.panelHeader}>
                        <span className={styles.panelTitle}>Duty Roster Report</span>
                        <div className={styles.panelClose} onClick={handleClose}>
                            <Cross />
                        </div>
                    </div>
                    <div className={`${styles.filterPopoverBody} FilterPopoverBody`}>
                        <div className="FilterPopoverBodyScroll">
                            <div className={styles.fieldGroup}>
                                {!isProjectControlled && (
                                    <div className={styles.fieldRow}>
                                        <span className={styles.fieldLabel}>Project</span>
                                        <div className={styles.fieldControl}>
                                            {innerProject ? (
                                                <div className={styles.selectedChip}>
                                                    <span className={styles.selectedChipLabel}>{innerProject.name}</span>
                                                    <span className={styles.selectedChipClear} onClick={handleClearProject}>
                                                        <X size={11} />
                                                    </span>
                                                </div>
                                            ) : (
                                                <CustomPopover
                                                    icon={
                                                        <div
                                                            className={styles.projectSearchTrigger}
                                                            onClick={() => {
                                                                setOpenDateRangePopover(false);
                                                                setOpenViewTypePopover(false);
                                                                setOpenProjectPopover((prev) => !prev);
                                                            }}
                                                        >

                                                            <span>Select project</span>
                                                            <ArrowDownMedium />
                                                        </div>
                                                    }
                                                    width="240px"
                                                    PopSurfaceClass={styles.calendarPopover}
                                                    status={openProjectPopover}
                                                >
                                                    <div className="p-2">
                                                        <IconInput
                                                            icon={<SearchIcon />}
                                                            placeholder="Search projects"
                                                            handleChange={setProjectSearch}
                                                        />
                                                    </div>
                                                    <div style={{ maxHeight: "200px", overflow: "auto" }}>
                                                        {projects.map((p) => (
                                                            <div
                                                                key={p.id}
                                                                className={styles.calendarPopoverItem}
                                                                onClick={() => handleSelectProject(p)}
                                                            >
                                                                {p.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </CustomPopover>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div className={styles.fieldRow}>
                                    <span className={styles.fieldLabel}>Period</span>
                                    <div className={styles.fieldControl}>
                                        <CustomPopover
                                            icon={
                                                <CustomButton
                                                    label={selectedDateRange.label}
                                                    btnClass={`${styles.selectBtn} d_btn`}
                                                    rightIcon={
                                                        <span className="ms-6 d-inline-flex">
                                                            <ArrowDownMedium />
                                                        </span>
                                                    }
                                                    clickHandler={() => {
                                                        setOpenProjectPopover(false);
                                                        setOpenViewTypePopover(false);
                                                        setOpenDateRangePopover((prev) => !prev);
                                                    }}
                                                />
                                            }
                                            width="140px"
                                            PopSurfaceClass={styles.calendarPopover}
                                            status={openDateRangePopover}
                                        >
                                            {DATE_RANGE_OPTIONS.map((opt) => (
                                                <div
                                                    key={opt.key}
                                                    className={styles.calendarPopoverItem}
                                                    onClick={() => {
                                                        setDateRangeKey(opt.key);
                                                        setOpenDateRangePopover(false);
                                                    }}
                                                >
                                                    {opt.label}
                                                </div>
                                            ))}
                                        </CustomPopover>
                                    </div>
                                </div>
                                {dateRangeKey === "custom" && (
                                    <>
                                        <div className={styles.fieldRow}>
                                            <span className={styles.fieldLabel}>From</span>
                                            <div className={styles.fieldControlStretch}>
                                                <DatePicker
                                                    value={customStartDate}
                                                    onSelectDate={setCustomStartDate}
                                                    placeholder="Start date"
                                                    maxDate={customEndDate || undefined}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.fieldRow}>
                                            <span className={styles.fieldLabel}>To</span>
                                            <div className={styles.fieldControlStretch}>
                                                <DatePicker
                                                    value={customEndDate}
                                                    onSelectDate={setCustomEndDate}
                                                    placeholder="End date"
                                                    minDate={customStartDate || undefined}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                                {/* <div className={styles.fieldRow}>
                                    <span className={styles.fieldLabel}>View</span>
                                    <div className={styles.fieldControl}>
                                        <CustomPopover
                                            icon={
                                                <CustomButton
                                                    icon={selectedViewType.icon}
                                                    label={selectedViewType.label}
                                                    btnClass={`${styles.selectBtn} d_btn`}
                                                    rightIcon={
                                                        <span className="ms-6 d-inline-flex">
                                                            <ArrowDownMedium />
                                                        </span>
                                                    }
                                                    clickHandler={() => {
                                                        setOpenProjectPopover(false);
                                                        setOpenDateRangePopover(false);
                                                        setOpenViewTypePopover((prev) => !prev);
                                                    }}
                                                />
                                            }
                                            width="140px"
                                            PopSurfaceClass={styles.calendarPopover}
                                            status={openViewTypePopover}
                                        >
                                            {VIEW_TYPES.map((v) => (
                                                <div
                                                    key={v.value}
                                                    className={styles.calendarPopoverItem}
                                                    onClick={() => {
                                                        setViewType(v.value);
                                                        setOpenViewTypePopover(false);
                                                    }}
                                                >
                                                    {v.icon}
                                                    {v.label}
                                                </div>
                                            ))}
                                        </CustomPopover>
                                    </div>
                                </div> */}
                            </div>

                            <div className={styles.toggleCard}>
                                <span className={styles.toggleLabel}>Show Weekend</span>
                                <Toggle checked={showWeekend} handleChange={setShowWeekend} StyleClass={styles.compactToggle} />
                            </div>
                            <div className={styles.toggleCard}>
                                <span className={styles.toggleLabel}>Show Utilization</span>
                                <Toggle checked={showUtilization} handleChange={setShowUtilization} StyleClass={styles.compactToggle} />
                            </div>

                            <div className={styles.sectionDivider} />

                            <div className={styles.fieldRow}>
                                <span className={styles.fieldLabel}>Resources</span>
                                <div className={styles.fieldControl}>
                                    <UserSelect
                                        selectedUsersDisplayMode="avatar-only"
                                        multiple
                                        disabled={!effectiveProjectId}
                                        refType={UserSearchRefType.Project}
                                        refId={effectiveProjectId}
                                        value={selectedResources}
                                        onChange={(users) => setSelectedResources(users || [])}
                                        buttonLabel="Add Resources"
                                        searchPlaceholder="Search resources"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.panelFooter}>
                        <CustomButton
                            btnClass="d_btn d_btn_white d_btn_whiteXsm"
                            label="Cancel"
                            icon={<X size={16} />}
                            clickHandler={handleClose}
                        />
                        <CustomButton
                            btnClass="d_btn d_btn_primary d_btn_primaryXsm"
                            label="Generate"
                            icon={<PrintIcon color="#fff" />}
                            clickHandler={handleReport}
                            showLoading={isLoading}
                            isDisabled={!effectiveProjectId || isCustomRangeInvalid}
                        />
                    </div>
                </div>
            </CustomPopover>
            {reportData && (
                <Modal
                    title={reportData.name}
                    handleCancel={() => setReportData(null)}
                    body={
                        reportData.pdfData && (
                            <iframe
                                title="PDF Viewer"
                                src={reportData.pdfData}
                                width="100%"
                                height="100%"
                            ></iframe>
                        )
                    }
                    type={true}
                    size=""
                />
            )}
        </div>
    );
}
